import { createRoute, lazyRouteComponent } from "@tanstack/react-router";
import { UserRole, UserType } from "@/types";
import { createProtectedLayoutRoute } from "./protectedLayouts";
import {
  assetOverviewSearchSchema,
  assetOverviewSearchMiddlewares,
  financiersSearchSchema,
  financiersSearchMiddlewares,
  manageUsersSearchSchema,
  manageUsersSearchMiddlewares,
  merchantsSearchSchema,
  merchantsSearchMiddlewares,
  poolActivitySearchSchema,
  poolActivitySearchMiddlewares,
  transactionsSearchSchema,
  transactionsSearchMiddlewares,
} from "@/routing/search";

const Dashboard = lazyRouteComponent(() => import("@/pages/admin/Dashboard/Page"));
const Financiers = lazyRouteComponent(() => import("@/pages/admin/Financiers/Page"));
const ManageUsers = lazyRouteComponent(() => import("@/pages/shared/ManageUsers/Page"));
const Merchants = lazyRouteComponent(() => import("@/pages/admin/Merchants/Page"));
const Transactions = lazyRouteComponent(() => import("@/pages/shared/Transactions/Page"));
const AssetOverview = lazyRouteComponent(() => import("@/pages/shared/AssetOverview/Page"));
const PoolActivity = lazyRouteComponent(() => import("@/pages/shared/PoolActivity/Page"));
const ScopedTransactions = lazyRouteComponent(() => import("@/pages/admin/ScopedTransactions/Page"));

const adminDevUserLayout = createProtectedLayoutRoute(
  "admin-dev-user",
  [UserRole.User, UserRole.Admin],
  [UserType.Developer],
);

export const adminDashboardRoute = createRoute({
  getParentRoute: () => adminDevUserLayout,
  path: "/admin/dashboard",
  component: Dashboard,
});

const adminDevAdminLayout = createProtectedLayoutRoute(
  "admin-dev-admin",
  [UserRole.Admin],
  [UserType.Developer],
);

export const adminManageUsersRoute = createRoute({
  getParentRoute: () => adminDevAdminLayout,
  path: "/admin/manageusers",
  validateSearch: manageUsersSearchSchema,
  search: { middlewares: manageUsersSearchMiddlewares },
  component: ManageUsers,
});

export const adminTransactionsRoute = createRoute({
  getParentRoute: () => adminDevAdminLayout,
  path: "/admin/transactions",
  validateSearch: transactionsSearchSchema,
  search: { middlewares: transactionsSearchMiddlewares },
  component: Transactions,
});

export const adminFinanciersRoute = createRoute({
  getParentRoute: () => adminDevAdminLayout,
  path: "/admin/financiers",
  validateSearch: financiersSearchSchema,
  search: { middlewares: financiersSearchMiddlewares },
  component: Financiers,
});

export const adminMerchantsRoute = createRoute({
  getParentRoute: () => adminDevAdminLayout,
  path: "/admin/merchants",
  validateSearch: merchantsSearchSchema,
  search: { middlewares: merchantsSearchMiddlewares },
  component: Merchants,
});

export const adminAssetOverviewRoute = createRoute({
  getParentRoute: () => adminDevAdminLayout,
  path: "/admin/assetoverview",
  validateSearch: assetOverviewSearchSchema,
  search: { middlewares: assetOverviewSearchMiddlewares },
  component: AssetOverview,
});

export const adminPoolActivityRoute = createRoute({
  getParentRoute: () => adminDevAdminLayout,
  path: "/admin/poolactivity",
  validateSearch: poolActivitySearchSchema,
  search: { middlewares: poolActivitySearchMiddlewares },
  component: PoolActivity,
});

export const adminMerchantTransactionsRoute = createRoute({
  getParentRoute: () => adminDevAdminLayout,
  path: "/admin/merchants/$merchantId/transactions",
  validateSearch: transactionsSearchSchema,
  search: { middlewares: transactionsSearchMiddlewares },
  component: ScopedTransactions,
});

export const adminFinancierTransactionsRoute = createRoute({
  getParentRoute: () => adminDevAdminLayout,
  path: "/admin/financiers/$financierId/transactions",
  validateSearch: transactionsSearchSchema,
  search: { middlewares: transactionsSearchMiddlewares },
  component: ScopedTransactions,
});

export const adminRoutes = [
  adminDevUserLayout.addChildren([adminDashboardRoute]),
  adminDevAdminLayout.addChildren([
    adminManageUsersRoute,
    adminTransactionsRoute,
    adminFinanciersRoute,
    adminFinancierTransactionsRoute,
    adminMerchantsRoute,
    adminMerchantTransactionsRoute,
    adminAssetOverviewRoute,
    adminPoolActivityRoute,
  ]),
];
